import { paginationMetaSchema } from '@steado/admin-contract/pagination';
import {
  adminWaitTipSchema,
  type AdminWaitTip,
  type AdminWaitTipCreateRequest,
  type AdminWaitTipUpdateRequest,
} from '@steado/admin-contract/wait-tips';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient } from '@/lib/api/client';

import type { WaitTipsSearch } from './wait-tips-search';

export const WAIT_TIPS_PAGE_SIZE = 20;

const waitTipsPageSchema = z.object({
  items: z.array(adminWaitTipSchema),
  pagination: paginationMetaSchema,
});
export type WaitTipsPage = z.infer<typeof waitTipsPageSchema>;

export function waitTipsQueryOptions(api: ApiClient, search: WaitTipsSearch) {
  const query = {
    locale: search.locale,
    // Absent means both, which is what the screen wants by default: a retired
    // tip is the most likely thing an operator is looking for.
    isActive: search.active === undefined ? undefined : search.active === 'live',
    cursor: search.cursor,
    limit: WAIT_TIPS_PAGE_SIZE,
  };
  return queryOptions({
    queryKey: ['admin', 'wait-tips', query],
    queryFn: async (): Promise<WaitTipsPage> => {
      const response = await api.requestWithMeta({
        path: '/admin/content/wait-tips',
        query,
        schema: z.array(adminWaitTipSchema),
      });
      return waitTipsPageSchema.parse({ items: response.data, pagination: response.meta.pagination });
    },
    placeholderData: (previous) => previous,
  });
}

export function createWaitTip(api: ApiClient, body: AdminWaitTipCreateRequest): Promise<AdminWaitTip> {
  return api.request({
    path: '/admin/content/wait-tips',
    method: 'POST',
    body,
    schema: adminWaitTipSchema,
  });
}

export function updateWaitTip(
  api: ApiClient,
  id: string,
  body: AdminWaitTipUpdateRequest,
): Promise<AdminWaitTip> {
  return api.request({
    path: `/admin/content/wait-tips/${id}`,
    method: 'PATCH',
    body,
    schema: adminWaitTipSchema,
  });
}
